import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { centsFromInput, moneyValue } from "./AiActionFields";
import {
  actionLineArrayFits,
  MAX_ADVANCEMENT_ORIGIN_LINES,
} from "./actionLimits";

const integerFormatter = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 0,
  useGrouping: true,
});
const monthFormatter = new Intl.DateTimeFormat("pt-BR", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const COMPETENCE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const EXECUTION_ENTRY_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,199}$/;

const validCompetence = (value) => COMPETENCE.test(String(value ?? ""));

const canonicalExecution = (execution, expectedModule) => {
  if (
    !execution ||
    Array.isArray(execution) ||
    typeof execution !== "object" ||
    Object.keys(execution).length !== 3 ||
    !["module", "entries", "affectedCompetences"].every((key) =>
      Object.prototype.hasOwnProperty.call(execution, key),
    ) ||
    execution.module !== expectedModule ||
    !actionLineArrayFits(execution.entries) ||
    !Array.isArray(execution.affectedCompetences) ||
    execution.affectedCompetences.length === 0 ||
    execution.affectedCompetences.length > MAX_ADVANCEMENT_ORIGIN_LINES + 1
  )
    return null;

  const validEntries = execution.entries.every(
    (entry) =>
      entry &&
      !Array.isArray(entry) &&
      typeof entry === "object" &&
      Object.keys(entry).length === 2 &&
      Object.prototype.hasOwnProperty.call(entry, "entryId") &&
      Object.prototype.hasOwnProperty.call(entry, "signedCents") &&
      typeof entry.entryId === "string" &&
      EXECUTION_ENTRY_ID.test(entry.entryId) &&
      Number.isSafeInteger(entry.signedCents) &&
      entry.signedCents !== 0,
  );
  const entryIds = execution.entries.map((entry) => entry.entryId);
  const competences = execution.affectedCompetences;
  const validCompetences =
    competences.every(validCompetence) &&
    new Set(competences).size === competences.length &&
    competences.every(
      (competence, index) =>
        index === 0 || competences[index - 1].localeCompare(competence) < 0,
    );

  return validEntries &&
    new Set(entryIds).size === entryIds.length &&
    validCompetences
    ? execution
    : null;
};

const projectionCompetence = (draft, execution) => {
  const preferredField =
    draft.module === "advancement" ? "targetCompetence" : "startCompetence";
  const preferred = [
    draft.proposal?.[preferredField],
    draft.fields?.[preferredField],
  ].find(validCompetence);
  return preferred ?? execution.affectedCompetences[0];
};

const projectionUrl = (draft, workId) => {
  const execution = canonicalExecution(draft?.execution, draft?.module);
  const normalizedWorkId = typeof workId === "string" ? workId.trim() : "";
  if (!execution || !normalizedWorkId) return null;
  const competence = projectionCompetence(draft, execution);
  return `/dashboard/projecao-financeira/${encodeURIComponent(
    normalizedWorkId,
  )}?aiMonth=${competence}&aiSource=${execution.module}`;
};

const currency = (cents) => {
  if (!Number.isSafeInteger(cents)) return "Sem valor";
  const rawCents = BigInt(cents);
  const absoluteCents = rawCents < 0n ? -rawCents : rawCents;
  const whole = absoluteCents / 100n;
  const fraction = String(absoluteCents % 100n).padStart(2, "0");
  return `R$ ${integerFormatter.format(whole)},${fraction}`.replace(
    /\u00a0/g,
    " ",
  );
};

const signedCurrency = (cents) => {
  if (!Number.isSafeInteger(cents)) return "Sem valor";
  if (cents > 0) return `+${currency(cents)}`;
  if (cents < 0) return `−${currency(cents)}`;
  return currency(0);
};

const monthLabel = (competence) => {
  const match = COMPETENCE.exec(String(competence ?? ""));
  if (!match) return "mês não informado";
  return monthFormatter.format(
    new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)),
  );
};

const monthAt = (startCompetence, offset) => {
  const match = COMPETENCE.exec(String(startCompetence ?? ""));
  if (!match) return null;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1 + offset, 1),
  );
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
    2,
    "0",
  )}`;
};

const MoneyInput = ({ label, cents, disabled, inputRef, onChange }) => {
  return (
    <label className="ai-action-proposal__edit-field">
      <span>{label}</span>
      <input
        ref={inputRef}
        type="number"
        inputMode="decimal"
        min="0.01"
        step="0.01"
        value={moneyValue(cents)}
        disabled={disabled}
        onChange={(event) => onChange(centsFromInput(event.target.value))}
      />
    </label>
  );
};

const editableOriginLines = (draft, editValues) => {
  const source = Array.isArray(editValues?.originLines)
    ? editValues.originLines
    : Array.isArray(draft?.proposal?.lines)
    ? draft.proposal.lines
    : [];
  return source.map((line) => ({
    candidateRef: String(line?.candidateRef ?? ""),
    fromCompetence: String(line?.fromCompetence ?? ""),
    amountCents: Number.isSafeInteger(line?.amountCents)
      ? line.amountCents
      : null,
  }));
};

const originBatchIsBalanced = (draft, editValues) => {
  if (draft?.module !== "advancement") return true;
  const amountCents = editValues?.amountCents;
  const lines = editableOriginLines(draft, editValues);
  const totalCents = lines.reduce((total, line) => total + line.amountCents, 0);
  return (
    Number.isSafeInteger(amountCents) &&
    amountCents > 0 &&
    actionLineArrayFits(lines) &&
    lines.every(
      (line) =>
        line.candidateRef &&
        validCompetence(line.fromCompetence) &&
        Number.isSafeInteger(line.amountCents) &&
        line.amountCents > 0,
    ) &&
    Number.isSafeInteger(totalCents) &&
    totalCents === amountCents
  );
};

const editableAllocationLines = (draft, editValues) => {
  let source;
  if (Array.isArray(editValues?.allocationLines)) {
    source = editValues.allocationLines;
  } else if (Array.isArray(draft?.proposal?.allocationLines)) {
    source = draft.proposal.allocationLines;
  } else if (draft?.module === "advancement" && Array.isArray(draft?.proposal?.lines)) {
    const totals = new Map();
    for (const line of draft.proposal.lines) {
      if (typeof line?.candidateRef !== "string" || !Number.isSafeInteger(line?.amountCents)) continue;
      totals.set(line.candidateRef, (totals.get(line.candidateRef) ?? 0) + line.amountCents);
    }
    source = [...totals].map(([candidateRef, amountCents]) => ({ candidateRef, amountCents }));
  } else if (draft?.proposal?.candidateRef && Number.isSafeInteger(draft?.proposal?.totalCents)) {
    source = [{
      candidateRef: draft.proposal.candidateRef,
      amountCents: draft.proposal.totalCents,
    }];
  } else {
    source = [];
  }
  return source.map((line) => ({
    candidateRef: String(line?.candidateRef ?? ""),
    amountCents: Number.isSafeInteger(line?.amountCents) ? line.amountCents : null,
  }));
};

const allocationState = (draft, editValues) => {
  const totalCents = draft?.module === "advancement"
    ? editValues?.amountCents
    : editValues?.totalCents;
  const lines = editableAllocationLines(draft, editValues);
  const selectedRefs = Array.isArray(editValues?.selectedCandidateRefs)
    ? editValues.selectedCandidateRefs
    : [];
  const refs = lines.map((line) => line.candidateRef);
  const allocatedCents = lines.reduce((total, line) => (
    Number.isSafeInteger(total) && Number.isSafeInteger(line.amountCents)
      ? total + line.amountCents
      : Number.NaN
  ), 0);
  const candidatesAreExact = selectedRefs.length === refs.length
    && new Set(selectedRefs).size === selectedRefs.length
    && new Set(refs).size === refs.length
    && selectedRefs.every((ref) => refs.includes(ref));
  const valid = Number.isSafeInteger(totalCents)
    && totalCents > 0
    && lines.length > 0
    && lines.length <= 20
    && candidatesAreExact
    && lines.every((line) => line.candidateRef
      && Number.isSafeInteger(line.amountCents)
      && line.amountCents > 0)
    && Number.isSafeInteger(allocatedCents)
    && allocatedCents === totalCents;
  return {
    allocatedCents: Number.isSafeInteger(allocatedCents) ? allocatedCents : 0,
    differenceCents: Number.isSafeInteger(totalCents) && Number.isSafeInteger(allocatedCents)
      ? totalCents - allocatedCents
      : null,
    lines,
    totalCents,
    valid,
  };
};

const equalAllocationLines = (totalCents, selectedRefs) => {
  if (!Number.isSafeInteger(totalCents) || totalCents < 1) return [];
  const refs = [...new Set(Array.isArray(selectedRefs) ? selectedRefs : [])]
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));
  if (refs.length < 1 || refs.length > 20) return [];
  const base = Math.floor(totalCents / refs.length);
  const remainder = totalCents % refs.length;
  return refs.map((candidateRef, index) => ({
    candidateRef,
    amountCents: base + (index < remainder ? 1 : 0),
  }));
};

const useAllocationRemovalFocus = ({
  lines,
  selectedRefs,
  disabled,
  onPatch,
}) => {
  const inputRefs = useRef(new Map());
  const regionRef = useRef(null);
  const focusAfterRemovalRef = useRef(undefined);
  const signature = lines.map((line) => line.candidateRef).join("\u0001");

  useEffect(() => {
    if (focusAfterRemovalRef.current === undefined || disabled) return;
    const nextRef = focusAfterRemovalRef.current;
    focusAfterRemovalRef.current = undefined;
    const target = nextRef ? inputRefs.current.get(nextRef) : regionRef.current;
    target?.focus();
  }, [disabled, signature]);

  const remove = (candidateRef, lineIndex) => {
    const nextRefs = selectedRefs.filter((ref) => ref !== candidateRef);
    focusAfterRemovalRef.current =
      nextRefs[Math.min(lineIndex, nextRefs.length - 1)] ?? null;
    onPatch({ selectedCandidateRefs: nextRefs });
  };

  const setInputRef = (candidateRef, node) => {
    if (node) inputRefs.current.set(candidateRef, node);
    else inputRefs.current.delete(candidateRef);
  };

  return { regionRef, remove, setInputRef };
};

const AdvancementProposal = ({
  draft,
  editValues,
  disabled,
  onPatch,
  onRecalculateOrigins,
}) => {
  const proposal = draft.proposal ?? {};
  const candidates = new Map(
    (Array.isArray(draft.candidates) ? draft.candidates : []).map(
      (candidate) => [candidate.ref, candidate],
    ),
  );
  const lines = editableOriginLines(draft, editValues);
  const sourceTotal = lines.reduce(
    (total, line) =>
      total + (Number.isSafeInteger(line.amountCents) ? line.amountCents : 0),
    0,
  );
  const destinationAmount = Number.isSafeInteger(editValues.amountCents)
    ? editValues.amountCents
    : Number.isSafeInteger(proposal.amountCents)
    ? proposal.amountCents
    : 0;
  const netCents = destinationAmount - sourceTotal;
  const selectedRefs = Array.isArray(editValues.selectedCandidateRefs)
    ? editValues.selectedCandidateRefs
    : [];
  const allocation = allocationState(draft, editValues);
  const allocationFocus = useAllocationRemovalFocus({
    lines: allocation.lines,
    selectedRefs,
    disabled,
    onPatch,
  });
  const diagnostics = new Map((Array.isArray(proposal.allocationDiagnostics)
    ? proposal.allocationDiagnostics
    : []).map((diagnostic) => [diagnostic.candidateRef, diagnostic]));
  const allocationsWithinCapacity = allocation.lines.every((line) => {
    const diagnostic = diagnostics.get(line.candidateRef);
    return diagnostic
      && Number.isSafeInteger(diagnostic.availableCents)
      && diagnostic.availableCents >= line.amountCents
      && (!Number.isSafeInteger(diagnostic.excessCents) || diagnostic.excessCents === 0);
  });
  const allocationIsValid = allocation.valid && allocationsWithinCapacity;
  const lineInputRefs = useRef(new Map());
  const recalculateRef = useRef(null);
  const focusAfterRemovalRef = useRef(undefined);
  const lineSignature = lines
    .map((line) => `${line.candidateRef}\u0000${line.fromCompetence}`)
    .join("\u0001");
  const balanced = originBatchIsBalanced(draft, editValues);

  useEffect(() => {
    if (focusAfterRemovalRef.current === undefined) return;
    const nextIdentity = focusAfterRemovalRef.current;
    focusAfterRemovalRef.current = undefined;
    const target = nextIdentity
      ? lineInputRefs.current.get(nextIdentity)
      : recalculateRef.current;
    target?.focus();
  }, [lineSignature]);

  const removeOriginLine = (line, lineIndex) => {
    const nextLines = lines.filter(
      (current) =>
        !(
          current.candidateRef === line.candidateRef &&
          current.fromCompetence === line.fromCompetence
        ),
    );
    const nextLine = nextLines[Math.min(lineIndex, nextLines.length - 1)];
    focusAfterRemovalRef.current = nextLine
      ? `${nextLine.candidateRef}\u0000${nextLine.fromCompetence}`
      : null;
    onPatch({ originLines: nextLines });
  };

  return (
    <>
      <section
        ref={allocationFocus.regionRef}
        className="ai-action-proposal__allocations"
        aria-label="Distribuição da antecipação por item EAP"
        tabIndex={-1}
      >
        <header>
          <div>
            <h5>Distribuição por item EAP</h5>
            <p>O valor de cada item será retirado apenas das suas competências disponíveis.</p>
          </div>
          <button
            type="button"
            className="ai-action-proposal__recalculate"
            aria-label="Redistribuir antecipação igualmente"
            disabled={disabled || selectedRefs.length === 0}
            onClick={() => onPatch({
              allocationLines: equalAllocationLines(destinationAmount, selectedRefs),
            })}
          >
            Redistribuir igualmente
          </button>
        </header>
        <div className="ai-action-proposal__allocation-list">
          {allocation.lines.map((line, lineIndex) => {
            const candidate = candidates.get(line.candidateRef);
            const label = candidate?.label || line.candidateRef;
            const diagnostic = diagnostics.get(line.candidateRef);
            return (
              <article className="ai-action-proposal__allocation" key={line.candidateRef}>
                <div className="ai-action-proposal__allocation-heading">
                  <strong>{label}</strong>
                  <button
                    type="button"
                    className="ai-action-proposal__remove"
                    disabled={disabled}
                    aria-label={`Remover item ${label} da distribuição`}
                    onClick={() => allocationFocus.remove(line.candidateRef, lineIndex)}
                  >
                    Remover
                  </button>
                </div>
                <MoneyInput
                  label={`Valor de ${label}`}
                  cents={line.amountCents}
                  disabled={disabled}
                  inputRef={(node) => allocationFocus.setInputRef(line.candidateRef, node)}
                  onChange={(amountCents) => onPatch({
                    allocationLines: allocation.lines.map((current) => (
                      current.candidateRef === line.candidateRef
                        ? { ...current, amountCents }
                        : current
                    )),
                  })}
                />
                <div className="ai-action-proposal__allocation-meta">
                  <span>{`Capacidade: ${currency(diagnostic?.availableCents)}`}</span>
                  <span>{`Proposto: ${currency(line.amountCents)}`}</span>
                  <span>{`Excesso: ${currency(diagnostic?.excessCents ?? 0)}`}</span>
                </div>
              </article>
            );
          })}
        </div>
        <div className="ai-action-proposal__allocation-balance" role="status" aria-live="polite">
          <span>{`Valor total: ${currency(allocation.totalCents)}`}</span>
          <span>{`Total alocado: ${currency(allocation.allocatedCents)}`}</span>
          <strong>{`Diferença: ${currency(Math.abs(allocation.differenceCents ?? 0))}`}</strong>
          <span>
            {allocationIsValid
              ? "Distribuição válida"
              : allocation.valid
                ? "Ajuste os itens com capacidade excedida"
                : "A distribuição precisa fechar exatamente"}
          </span>
        </div>
      </section>
      <div className="ai-action-proposal__flow">
        <section aria-label="Reduções nas origens">
          <h5>Reduzir nas origens</h5>
          <ul>
            {lines.map((line) => {
              const candidate = candidates.get(line.candidateRef);
              return (
                <li key={`${line.candidateRef}:${line.fromCompetence}`}>
                  <div>
                    <strong>{candidate?.label || line.candidateRef}</strong>
                    <span>{monthLabel(line.fromCompetence)}</span>
                  </div>
                  <b>{signedCurrency(-line.amountCents)}</b>
                </li>
              );
            })}
          </ul>
        </section>
        <span className="ai-action-proposal__arrow" aria-hidden="true">
          →
        </span>
        <section aria-label="Adição no destino">
          <h5>Adicionar no destino</h5>
          <div className="ai-action-proposal__destination">
            <span>
              {monthLabel(
                editValues.targetCompetence ?? proposal.targetCompetence,
              )}
            </span>
            <strong>{signedCurrency(destinationAmount)}</strong>
          </div>
        </section>
      </div>
      <div className="ai-action-proposal__balance" role="status">
        <span>Efeito líquido</span>
        <strong>{signedCurrency(netCents)}</strong>
      </div>
      {!balanced ? (
        <p
          className="ai-action-proposal__imbalance"
          role="status"
          aria-live="polite"
        >
          O lote está desequilibrado. Ajuste as linhas até o efeito líquido
          voltar a zero antes de confirmar.
        </p>
      ) : null}
      <div
        className="ai-action-proposal__editing"
        aria-label="Editar proposta de antecipação"
      >
        <MoneyInput
          label={`Valor de ${monthLabel(editValues.targetCompetence)}`}
          cents={editValues.amountCents}
          disabled={disabled}
          onChange={(amountCents) => onPatch({ amountCents })}
        />
        <label className="ai-action-proposal__edit-field">
          <span>Mês de destino</span>
          <input
            type="month"
            value={editValues.targetCompetence ?? ""}
            disabled={disabled}
            onChange={(event) =>
              onPatch({ targetCompetence: event.target.value || null })
            }
          />
        </label>
        <fieldset className="ai-action-proposal__origin-lines">
          <legend>Linhas de origem</legend>
          {lines.map((line, lineIndex) => {
            const candidateLabel =
              candidates.get(line.candidateRef)?.label || line.candidateRef;
            const lineLabel = `${monthLabel(
              line.fromCompetence,
            )} de ${candidateLabel}`;
            return (
              <div
                className="ai-action-proposal__origin-line"
                key={`${line.candidateRef}:${line.fromCompetence}`}
              >
                <MoneyInput
                  label={`Valor retirado de ${lineLabel}`}
                  cents={line.amountCents}
                  disabled={disabled}
                  inputRef={(node) => {
                    const identity = `${line.candidateRef}\u0000${line.fromCompetence}`;
                    if (node) lineInputRefs.current.set(identity, node);
                    else lineInputRefs.current.delete(identity);
                  }}
                  onChange={(amountCents) =>
                    onPatch({
                      originLines: lines.map((current) =>
                        current.candidateRef === line.candidateRef &&
                        current.fromCompetence === line.fromCompetence
                          ? { ...current, amountCents }
                          : current,
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeOriginLine(line, lineIndex)}
                >
                  Remover linha de {lineLabel}
                </button>
              </div>
            );
          })}
          <button
            ref={recalculateRef}
            type="button"
            className="ai-action-proposal__recalculate"
            disabled={disabled}
            onClick={onRecalculateOrigins}
          >
            Recalcular origens automaticamente
          </button>
        </fieldset>
        <div className="ai-action-proposal__sources">
          <span>Origens incluídas</span>
          {selectedRefs.map((ref) => (
            <button
              type="button"
              key={ref}
              disabled={disabled}
              onClick={() =>
                onPatch({
                  selectedCandidateRefs: selectedRefs.filter(
                    (selectedRef) => selectedRef !== ref,
                  ),
                })
              }
            >
              Remover {candidates.get(ref)?.label || ref}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

const ExtraValueProposal = ({ draft, editValues, disabled, onPatch }) => {
  const proposal = draft.proposal ?? {};
  const installments = Array.isArray(proposal.installmentCents)
    ? proposal.installmentCents
    : [];
  const selectedRefs = Array.isArray(editValues.selectedCandidateRefs)
    ? editValues.selectedCandidateRefs
    : [];
  const candidates = new Map((Array.isArray(draft.candidates) ? draft.candidates : [])
    .map((item) => [item.ref, item]));
  const serverAllocations = new Map((Array.isArray(proposal.allocationLines)
    ? proposal.allocationLines
    : []).map((line) => [line.candidateRef, line]));
  const allocation = allocationState(draft, editValues);
  const allocationFocus = useAllocationRemovalFocus({
    lines: allocation.lines,
    selectedRefs,
    disabled,
    onPatch,
  });
  const candidateLabel = (ref) => {
    const server = serverAllocations.get(ref);
    return candidates.get(ref)?.label
      || server?.path
      || server?.name
      || ref;
  };

  return (
    <>
      <div className="ai-action-proposal__extra-summary">
        <div>
          <span>
            {proposal.kind === "saving"
              ? "Economia prevista"
              : "Despesa prevista"}
          </span>
          <strong>{signedCurrency(proposal.signedTotalCents)}</strong>
        </div>
        <p>
          {candidates.get(proposal.candidateRef)?.label ||
            [proposal.path, proposal.name].filter(Boolean).join(" — ")}
        </p>
        <p>{proposal.description}</p>
      </div>
      <ol
        className="ai-action-proposal__installments"
        aria-label="Parcelas com sinal financeiro"
      >
        {installments.map((signedCents, index) => {
          const competence = monthAt(proposal.startCompetence, index);
          return (
            <li key={competence}>
              {monthLabel(competence)}: {signedCurrency(signedCents)}
            </li>
          );
        })}
      </ol>
      <section
        ref={allocationFocus.regionRef}
        className="ai-action-proposal__allocations"
        aria-label="Distribuição por item EAP"
        tabIndex={-1}
      >
        <header>
          <div>
            <h5>Distribuição por item EAP</h5>
            <p>Revise cada valor antes da execução.</p>
          </div>
          <button
            type="button"
            className="ai-action-proposal__recalculate"
            disabled={disabled || selectedRefs.length === 0}
            onClick={() => onPatch({
              allocationLines: equalAllocationLines(
                editValues.totalCents ?? proposal.totalCents,
                selectedRefs,
              ),
            })}
          >
            Redistribuir igualmente
          </button>
        </header>
        <div className="ai-action-proposal__allocation-list">
          {allocation.lines.map((line, lineIndex) => {
            const label = candidateLabel(line.candidateRef);
            const server = serverAllocations.get(line.candidateRef);
            const percentage = Number.isSafeInteger(allocation.totalCents)
              && allocation.totalCents > 0
              ? (line.amountCents / allocation.totalCents) * 100
              : 0;
            return (
              <article className="ai-action-proposal__allocation" key={line.candidateRef}>
                <div className="ai-action-proposal__allocation-heading">
                  <div>
                    <strong>{label}</strong>
                    {server?.path && server.path !== label ? <small>{server.path}</small> : null}
                  </div>
                  <button
                    type="button"
                    className="ai-action-proposal__remove"
                    disabled={disabled}
                    aria-label={`Remover ${label}`}
                    onClick={() => allocationFocus.remove(line.candidateRef, lineIndex)}
                  >
                    Remover
                  </button>
                </div>
                <MoneyInput
                  label={`Valor de ${label}`}
                  cents={line.amountCents}
                  disabled={disabled}
                  inputRef={(node) => allocationFocus.setInputRef(line.candidateRef, node)}
                  onChange={(amountCents) => onPatch({
                    allocationLines: allocation.lines.map((current) => (
                      current.candidateRef === line.candidateRef
                        ? { ...current, amountCents }
                        : current
                    )),
                  })}
                />
                <div className="ai-action-proposal__allocation-meta">
                  <span>{`${percentage.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })}% do total`}</span>
                  <span>{`${proposal.installments ?? 0} parcelas`}</span>
                  <span>{`Competências: ${monthLabel(proposal.startCompetence)} a ${monthLabel(proposal.endCompetence)}`}</span>
                  {server?.installmentCents?.length ? (
                    <span>{`Primeira parcela: ${signedCurrency(server.installmentCents[0])}`}</span>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
        <div className="ai-action-proposal__allocation-balance" role="status" aria-live="polite">
          <span>{`Valor total: ${currency(allocation.totalCents)}`}</span>
          <span>{`Total alocado: ${currency(allocation.allocatedCents)}`}</span>
          <strong>{`Diferença: ${currency(Math.abs(allocation.differenceCents ?? 0))}`}</strong>
          <span>{allocation.valid ? "Distribuição válida" : "A distribuição precisa fechar exatamente"}</span>
        </div>
      </section>
      <div
        className="ai-action-proposal__editing"
        aria-label="Editar proposta de estouro ou economia"
      >
        <label className="ai-action-proposal__edit-field">
          <span>Tipo do ajuste</span>
          <select
            value={editValues.kind ?? ""}
            disabled={disabled}
            onChange={(event) => onPatch({ kind: event.target.value || null })}
          >
            <option value="expense">Despesa</option>
            <option value="saving">Economia</option>
          </select>
        </label>
        <MoneyInput
          label="Valor total"
          cents={editValues.totalCents}
          disabled={disabled}
          onChange={(totalCents) => onPatch({ totalCents })}
        />
        <label className="ai-action-proposal__edit-field">
          <span>Mês inicial</span>
          <input
            type="month"
            value={editValues.startCompetence ?? ""}
            disabled={disabled}
            onChange={(event) =>
              onPatch({ startCompetence: event.target.value || null })
            }
          />
        </label>
        <label className="ai-action-proposal__edit-field">
          <span>Número de parcelas</span>
          <input
            type="number"
            min="1"
            max="36"
            step="1"
            value={editValues.installments ?? ""}
            disabled={disabled}
            onChange={(event) => {
              const installmentsValue = Number(event.target.value);
              onPatch({
                installments:
                  Number.isSafeInteger(installmentsValue) &&
                  installmentsValue >= 1 &&
                  installmentsValue <= 36
                    ? installmentsValue
                    : null,
              });
            }}
          />
        </label>
        <label className="ai-action-proposal__edit-field ai-action-proposal__edit-field--wide">
          <span>Descrição</span>
          <textarea
            maxLength={500}
            value={editValues.description ?? ""}
            disabled={disabled}
            onChange={(event) =>
              onPatch({ description: event.target.value || null })
            }
          />
        </label>
      </div>
    </>
  );
};

const AiActionProposal = ({
  draft,
  workId,
  editValues = draft?.fields ?? {},
  busy = false,
  confirmationBlocked = false,
  confirming = false,
  requiresReview = false,
  onPatch,
  onConfirm,
  onReviewed,
  onRecalculateOrigins,
}) => {
  const confirmLatch = useRef(false);
  const [localConfirming, setLocalConfirming] = useState(false);
  const navigate = useNavigate();

  if (!draft) return null;
  if (draft.state === "expired") {
    return (
      <p className="ai-action-proposal__notice" role="alert">
        O rascunho expirou. Monte uma nova proposta.
      </p>
    );
  }
  if (draft.state === "executed") {
    const href = projectionUrl(draft, workId);
    return (
      <section
        className="ai-action-proposal ai-action-proposal--executed"
        aria-label="Resultado da ação executada"
      >
        <h4>Ação executada</h4>
        <p>
          {draft.execution?.affectedCompetences?.length
            ? `Competências afetadas: ${draft.execution.affectedCompetences
                .map(monthLabel)
                .join(", ")}.`
            : "A atualização financeira foi concluída."}
        </p>
        {href ? (
          <a
            href={href}
            className="ai-action-proposal__projection-link"
            onClick={(event) => {
              event.preventDefault();
              navigate(href);
            }}
          >
            Ver na projeção financeira
          </a>
        ) : null}
      </section>
    );
  }
  if (draft.module !== "advancement" && draft.module !== "extra_value") {
    return (
      <p className="ai-action-proposal__notice" role="alert">
        Módulo de ação não reconhecido.
      </p>
    );
  }

  const allocation = allocationState(draft, editValues);
  const balanced = allocation.valid && originBatchIsBalanced(draft, editValues);
  const confirm = async () => {
    if (confirmLatch.current || busy || requiresReview || !balanced) return;
    confirmLatch.current = true;
    setLocalConfirming(true);
    try {
      await onConfirm();
    } finally {
      confirmLatch.current = false;
      setLocalConfirming(false);
    }
  };
  const disabled = busy || localConfirming;

  return (
    <section
      className="ai-action-proposal"
      aria-label="Proposta financeira para revisão"
    >
      <header>
        <span>Proposta calculada pelo Ampli</span>
        <h4>Revise antes de executar</h4>
      </header>
      {draft.missingFields?.includes("originLines") && draft.explanation ? (
        <p className="ai-action-proposal__notice" role="alert">
          {draft.explanation}
        </p>
      ) : null}
      {draft.module === "advancement" ? (
        <AdvancementProposal
          draft={draft}
          editValues={editValues}
          disabled={disabled}
          onPatch={onPatch}
          onRecalculateOrigins={onRecalculateOrigins}
        />
      ) : (
        <ExtraValueProposal
          draft={draft}
          editValues={editValues}
          disabled={disabled}
          onPatch={onPatch}
        />
      )}
      {requiresReview ? (
        <button
          type="button"
          className="ai-action-proposal__review"
          disabled={busy}
          onClick={onReviewed}
        >
          Revisei a proposta atualizada
        </button>
      ) : null}
      {draft.state === "ready_for_review" ? (
        <p className="ai-action-proposal__execution-summary" role="status">
          {`${allocation.lines.length} ${allocation.lines.length === 1 ? "item" : "itens"} · ${currency(allocation.totalCents)} · ${allocation.lines.length} ${allocation.lines.length === 1 ? "entrada" : "entradas"} · execução atômica`}
        </p>
      ) : null}
      <button
        type="button"
        className="ai-action-proposal__confirm"
        disabled={
          disabled ||
          confirmationBlocked ||
          !balanced ||
          requiresReview ||
          draft.state !== "ready_for_review"
        }
        onClick={confirm}
      >
        {localConfirming || confirming ? "Executando…" : "Confirmar e executar"}
      </button>
    </section>
  );
};

export { currency, monthLabel, signedCurrency };
export default AiActionProposal;
